import { NgModule } from '@angular/core';
import { provideConfig } from '../../../core/config/config-providers';

@NgModule({
  providers: [
    provideConfig({
      api: {
        endpoints: {
          profile: 'admin/profile/${profileId}',
          profiles: 'admin/profiles',
          profileTemplate: 'admin/profile/<%= profileId %>/template',
          profileTemplates: 'admin/profile-templates',
          users: 'admin/users',
          newuser: 'admin/user',
          user: 'admin/user/${userNumber}',
          profileUsers: 'admin/profile/${profileId}/users',
          profileUser: 'admin/profile/${profileId}/user/${userNumber}',
          roles: 'admin/roles/${profile}',
          currentUserRoles: 'self/profile/${profileId}/roles',
          resetPassword: 'admin/user/${userNumber}/resetPassword',
          changeSelfPassword: 'self/changePassword',
          profileLogo: 'admin/profile/${profileId}/logo',
          uploadProfileLogo: 'admin/profile/${profileId}/logo',
          deleteProfileLogo: 'admin/profile/${profileId}/logo',
          generateAdhocFallbackCustomer: 'admin/profile/${profileId}/adhocFallbackCustomer/generate',
          systemGateways: 'systems/gateways',
          systemBins: 'systems/bins',
          systemBinCreate: 'systems/bin',
          systemBin: 'systems/bin/${name}',
          systemBinLookup: 'systems/lookup/bin/${number}',
          profileApiKey: 'admin/profile/${profileId}/keys/${keyName}',
          validateApiKey: 'admin/user/${profileId}/keys/${keyName}/validate',
          deleteApiKey: 'admin/user/${profileId}/keys/${keyName}',
          schedulerJobs: 'scheduler/jobs',
          schedulerJob: 'scheduler/jobs/${jobId}',
          schedulerJobRun: 'scheduler/jobs/${jobId}/run',
          schedulerJobReset: 'scheduler/jobs/${jobId}/reset',
          schedulerJobEnable: 'scheduler/jobs/${jobId}/enable',
          schedulerJobDisable: 'scheduler/jobs/${jobId}/disable',
          schedulerJobHistory: 'scheduler/jobs/${jobId}/history',
          schedulerHistory: 'scheduler/history',
          schedulerCommands: 'scheduler/commands',
          schedulerCommandGroup: 'scheduler/commands/${groupName}',
          erpImportDefinitions: 'integrations/erp/imports',
          erpImportDefinition: 'integrations/erp/imports/${importDefinitionId}',
          erpImportSchedule: 'integrations/erp/imports/${importDefinitionId}/schedule',
          erpImportSupervisors: 'integrations/erp/supervisors',
          erpImportSupervisor: 'integrations/erp/supervisors/${supervisorId}',
          erpImportSupervisorDefinitions: 'integrations/erp/supervisors/${supervisorId}/definitions',
          erpImportSupervisorRun: 'integrations/erp/supervisors/${supervisorId}/run',
          erpImportSupervisorSchedule: 'integrations/erp/supervisors/${supervisorId}/schedule',
          erpImportSupervisorRuns: 'integrations/erp/supervisors/${supervisorId}/runs',
          erpExportDefinitions: 'integrations/erp/exports',
          erpExportDefinition: 'integrations/erp/exports/${exportDefinitionId}',
          sftpConnections: 'integrations/sftp/connections',
          erpApiConnectors: 'integrations/erp/api-connectors',
          erpApiConnections: 'integrations/erp/api-connections',
          erpApiConnection: 'integrations/erp/api-connections/${connectionId}',
          erpApiConnectionCredentials: 'integrations/erp/api-connections/${connectionId}/credentials',
          erpApiConnectionTest: 'integrations/erp/api-connections/${connectionId}/test',
          erpApiConnectionSync: 'integrations/erp/api-connections/${connectionId}/sync',
          erpApiConnectionSyncSchedule: 'integrations/erp/api-connections/${connectionId}/sync-schedule',
          erpApiPostingIssues: 'integrations/erp/api-posting-issues',
          erpApiPostingIssueResolve: 'integrations/erp/api-posting-issues/${freelyPaymentId}/resolve',
          erpApiReversalsScan: 'integrations/erp/api-reversals/scan',
          erpApiReversalRebillPreview: 'integrations/erp/api-reversals/${freelyPaymentId}/rebill-preview',
          erpApiReversalRebill: 'integrations/erp/api-reversals/${freelyPaymentId}/rebill',
          sftpFreelyAvailability: 'integrations/sftp/freely-availability',
          sftpConnection: 'integrations/sftp/connections/${connectionId}',
          sftpConnectionTest: 'integrations/sftp/connections/${connectionId}/test',
          sftpConnectionFolderListing: 'integrations/sftp/connections/${connectionId}/folder-listing',
          // Folder browser (ERP dashboard). Distinct from folderListing above, which is the System
          // Admin dialog's remote+archive summary and stays on its own contract.
          sftpBrowsableConnections: 'integrations/sftp/connections/browsable',
          sftpConnectionBrowseFolders: 'integrations/sftp/connections/${connectionId}/folders',
          sftpConnectionBrowseFiles: 'integrations/sftp/connections/${connectionId}/files',
          sftpConnectionFileDownload: 'integrations/sftp/connections/${connectionId}/files/download',
          sftpConnectionFileMove: 'integrations/sftp/connections/${connectionId}/files/move',
          // Chunked folder upload — any size; Cloud Run refuses a single request over 32 MiB.
          sftpFolderUploads: 'integrations/sftp/connections/${connectionId}/uploads',
          sftpFolderUpload: 'integrations/sftp/connections/${connectionId}/uploads/${uploadId}',
          sftpFolderUploadPart: {
            path: 'integrations/sftp/connections/${connectionId}/uploads/${uploadId}/parts/${partNumber}',
            headers: {},
          },
          sftpFolderUploadComplete: 'integrations/sftp/connections/${connectionId}/uploads/${uploadId}/complete',
          sftpConnectionCredentials: 'integrations/sftp/connections/${connectionId}/credentials',
          freelySftpConnections: 'system-admin/sftp/connections',
          fileSysSettings: 'system/filesys/settings',
          freelySftpConnection: 'system-admin/sftp/connections/${connectionId}',
          freelySftpConnectionTest: 'system-admin/sftp/connections/${connectionId}/test',
          freelySftpConnectionCredentials: 'system-admin/sftp/connections/${connectionId}/credentials',
          sftpGatewayProfileUser: 'admin/profile/${profileId}/sftp-user',
          sftpGatewayProfileUserReset: 'admin/profile/${profileId}/sftp-user/reset-password',
          // {profileId}-Sync — the account OTHER Freely environments sync this profile through.
          sftpGatewayProfileSyncUser: 'admin/profile/${profileId}/sftp-sync-user',
          sftpGatewayProfileSyncUserReset: 'admin/profile/${profileId}/sftp-sync-user/reset-password',
          // This profile's accounts on other Freely environments' gateways (SYSTEM_ADMIN).
          freelySftpServers: 'system-admin/sftp/freely-servers',
          remoteSftpAccounts: 'admin/profile/${profileId}/remote-sftp-accounts',
          remoteSftpAccount: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}',
          remoteSftpAccountPassword: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/password',
          remoteSftpAccountSyncPassword: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/sync-password',
          remoteSftpAccountSyncs: 'admin/profile/${profileId}/remote-sftp-accounts/syncs',
          remoteSftpAccountSync: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/sync',
          remoteSftpAccountSyncRun: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/sync/run',
          remoteSftpAccountSyncRuns: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/sync/runs',
          remoteSftpAccountSyncFiles: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/sync/files',
          remoteSftpAccountSyncFile: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/sync/files/${syncId}',
          remoteSftpAccountTest: 'admin/profile/${profileId}/remote-sftp-accounts/${connectionId}/test',
          // Windows File Util installations — SYSTEM_ADMIN or WINDOWS_FILE_UTIL_MANAGE on the path profile.
          windowsFileUtilAccounts: 'admin/profile/${profileId}/windows-file-util/accounts',
          windowsFileUtilAccount: 'admin/profile/${profileId}/windows-file-util/accounts/${installationName}',
          windowsFileUtilBundle: 'admin/profile/${profileId}/windows-file-util/bundle',
          sftpProfileUsers: 'system-admin/sftp-profile-users',
          sftpGatewayAccounts: 'system-admin/sftp-accounts',
          sftpGatewayAccountReset: 'system-admin/sftp-accounts/${userId}/reset-password',
          sftpGatewayAccountDisable: 'system-admin/sftp-accounts/${userId}/disable',
          emailTemplateTypes: 'admin/email-templates/types',
          emailTemplatePreview: 'admin/email-templates/preview',
          emailTemplatesForProfile: 'admin/email-templates/${profileId}',
          emailTemplate: 'admin/email-templates/${profileId}/${templateType}',
          emailTemplatesExport: 'admin/email-templates/${profileId}/export',
          emailTemplatesImport: 'admin/email-templates/${profileId}/import',
          adminStartDeleteProfileJob: 'admin/profile/${profileId}/delete-job',
          adminGetDeleteProfileJob: 'admin/jobs/${jobId}',
          adminStartTruncateProfileJob: 'admin/profile/${profileId}/truncate-job',
          adminGetTruncateProfileJob: 'admin/truncate-jobs/${jobId}',
          // System-admin destructive: purge ERP data, keeping Freely payments, so history can be reimported
          adminPreviewErpPurge: 'admin/profile/${profileId}/erp-purge/preview',
          adminStartErpPurgeJob: 'admin/profile/${profileId}/erp-purge-job',
          adminGetErpPurgeJob: 'admin/erp-purge-jobs/${jobId}',
          // Customer ledger: solve every customer's opening balance forward as a
          // background job. Not destructive, and not non-prod only.
          startBalanceForwardJob: 'customer-ledger/balance-forward-job',
          getBalanceForwardJob: 'customer-ledger/balance-forward-jobs/${jobId}',
          customerLedgerResetStatus: 'customer-ledger/reset',
          startCustomerLedgerResetJob: 'customer-ledger/reset-job',
          getCustomerLedgerResetJob: 'customer-ledger/reset-jobs/${jobId}',
          adminProfileTimeZone: 'admin/profile/${profileId}/timezone',
          adminProfileAutopaySettings:
            'admin/profile/${profileId}/autopay/settings',
          adminProfileAutopayTermRules:
            'admin/profile/${profileId}/autopay/term-rules',
          adminProfileAutopayEnrollmentPreview:
            'admin/profile/${profileId}/autopay/enrollment-preview',
          adminProfileAutopayMockTokens:
            'admin/profile/${profileId}/autopay/mock-tokens',
          adminProfileAutopayRecalcPreview:
            'admin/profile/${profileId}/autopay/recalc-preview',
          adminProfileAutopayRecalcJob:
            'admin/profile/${profileId}/autopay/recalc-job',
          adminGetAutopayRecalcJob: 'admin/autopay-recalc-jobs/${jobId}',
          adminProfileAutopayEnrollmentApply:
            'admin/profile/${profileId}/autopay/enrollment-apply',
          adminProfileAutopayExceptions:
            'admin/profile/${profileId}/autopay/exceptions',
          adminProfileAutopayRun: 'admin/profile/${profileId}/autopay/run',
          adminProfileLookupValues:
            'admin/profile/${profileId}/lookups/${valueType}',
          taxRateStateLookup: 'tax-rates/state-lookup',
          processorTypes: 'admin/processorTypes',
          processorSettings: 'admin/profile/${profileId}/processorSettings',
          processorSetting: 'admin/profile/${profileId}/processorSettings/${processor}/${accountId}',
          payaOnboardingParse: 'admin/profile/${profileId}/paya/parse-onboarding-pdf',
          // Captured (test-mode) emails for the active profile. Profile-scoped and
          // gated on TEST_GET_EMAILS — distinct from the SYSTEM_ADMIN-only
          // /admin/history/emails audit of every email ever sent.
          adminProfileTestEmails: 'admin/history/test-emails',
          adminProfileTestEmailForward: 'admin/history/test-emails/${emailAuditId}/forward',
        },
      },
    }),
  ],
})
export class ConfigModule {}
